import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import { FiRefreshCw, FiAlertCircle } from "react-icons/fi";
import { useFormikContext } from "formik";

import SolicitationNew from "../../../../../../presentation/pages/admin/solicitation/new";
import NfseNacionalCard from "../../../../../../presentation/components/nfse-nacional-card";

import { IPackage } from "../../../../../../presentation/interfaces";

import { makeRemoteStack } from "../../../../usecases/stack/remote-stack-factory";
import { makeRemotePackage } from "../../../../usecases/package/remote-package-factory";
import { makeRemoteDeveloper } from "../../../../usecases/developer/remote-developer-factory";
import { makeRemoteCompanies } from "../../../../usecases/companies/remote-companies-factory";

import {
  taxTypeList,
  especialTaxTypeList,
  planStatusList,
  addressTypeList,
} from "../../../../../../utils";

import {
  handleCepChange,
  handleNoStackChange,
  getErrorMessage,
  makeDeveloper,
  makeCompany,
  makeCertificate,
  hasCertificate,
  showErrorMessage,
  showWarningMessage,
  showSuccessMessage,
  handleCnpjChange,
  extractNumbers,
} from "../../../../../../presentation/helpers";
import { applyNamedMask } from "../../../../../../utils/mask";
import { HelperCnpjInformaton } from "../../../../../../presentation/helpers/helper-cnpj-information";
import HelperMask from "../../../../../../presentation/helpers/helper-mask";
import toastUtils from "../../../../../../utils/ToastUtils";
import {
  ConfirmDialogStyled,
  SyncButton,
} from "../../../../../../presentation/pages/developer/profile/render-form/styles";
import { theme } from "../../../../../../presentation/styles/theme";

const MakeSolicitationNew: React.FC = () => {
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = (): void => setIsSmallScreen(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const history = useHistory();

  const [errorMessage] = useState<string | null>("");

  const [packages, setPackages] = useState<IPackage[]>([]);

  const [stacks, setStacks] = useState<any[]>([]);

  const [resyncing, setResyncing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingCnpj, setPendingCnpj] = useState("");
  const companyCnpjRef = useRef<string>("");
  const setFieldValueRef = useRef<((field: string, value: any) => void) | null>(null);

  useEffect(() => {
    const fetchStacks = async (): Promise<void> => {
      const response = await makeRemoteStack().loadAll();
      setStacks(response);
    };
    fetchStacks();
  }, []);

  useEffect(() => {
    const fetchPackages = async (): Promise<void> => {
      const remotePackage = makeRemotePackage();
      try {
        const response = await remotePackage.loadAll();
        setPackages(response);
      } catch (error) {
        console.error("Error fetching packages:", error);
      }
    };

    fetchPackages();
  }, []);

  const makeSolicitation = useCallback((data: any): any => ({
    ...makeDeveloper(data),
    company: makeCompany(data),
    pack: {
      id: data?.plan,
    },
    planStatus: data?.planStatus,
  }), []);

  const handleError = useCallback((error: any): void => {
    showWarningMessage(getErrorMessage(error));
  }, []);

  const uploadCertificate = useCallback(async (
    data: any,
    companyId?: number,
  ): Promise<void> => {
    if (!hasCertificate(data)) return;

    try {
      await makeRemoteCompanies().createCertificate(
        makeCertificate(data, String(companyId)),
      );
    } catch (error: any) {
      handleError(error);
    }
  }, [handleError]);

  const handleSubmit = useCallback(async (data: any): Promise<void> => {
    try {
      const dataToSend = makeSolicitation(data);

      const createdSolicitation = await makeRemoteDeveloper().adminCreate(dataToSend);

      showSuccessMessage("Solicitação criada com sucesso");

      await uploadCertificate(data, createdSolicitation?.company?.id);

      history.replace(`/admin/developers/edit/${createdSolicitation.id}`);
    } catch (error: any) {
      console.error(error);
      showErrorMessage(getErrorMessage(error));
    }
  }, [history, uploadCertificate, makeSolicitation]);

  const goBack = useCallback((): void => {
    history.goBack();
  }, [history]);

  const executeSync = useCallback(async (cnpj: string, setFieldValue: (field: string, value: any) => void): Promise<void> => {
    try {
      setResyncing(true);
      const response = await HelperCnpjInformaton.getInformationCnpj(cnpj);
      if (!response) return;

      const newActivities = response.atividades?.map((item: any) => ({
        codigo: item.codigo,
        descricao: item.descricao,
        tipo: item.tipo,
      }));

      setFieldValue("socialReason", response.razao_social || "");
      setFieldValue("fantasyName", response.nome?.trim() || response.razao_social || "");
      setFieldValue("companyEmail", response.email || "");
      setFieldValue("companyActivities", newActivities || []);
      setFieldValue("companyCep", response.endereco?.cep ? applyNamedMask(response.endereco.cep, "CEP") : "");
      setFieldValue("companyStreet", response.endereco?.logradouro || "");
      setFieldValue("companyNumber", response.endereco?.numero || "");
      setFieldValue("companyNeighborhood", response.endereco?.bairro || "");
      setFieldValue("companyCity", response.endereco?.municipio || "");
      setFieldValue("companyState", response.endereco?.uf || "");
      setFieldValue("companyComplement", (response.endereco as any)?.complemento || "");

      if (response.telefone) {
        response.telefone.split("/").forEach((segment: string) => {
          const cleanedNumber = extractNumbers(segment);
          if (cleanedNumber.length === 10) {
            setFieldValue("companyPhone", applyNamedMask(cleanedNumber, "PHONE_NUMBER"));
          } else if (cleanedNumber.length === 11) {
            setFieldValue("companyMobilePhone", applyNamedMask(cleanedNumber, "MOBILE_NUMBER"));
          }
        });
      }

      companyCnpjRef.current = cnpj;

      toastUtils.showMessage({
        type: "success",
        message: "Informações sincronizadas com sucesso!",
      });
    } catch (error) {
      toastUtils.showMessage({
        type: "error",
        message: "Erro ao buscar informações do CNPJ.",
      });
    } finally {
      setResyncing(false);
    }
  }, []);

  const SyncButtonComponent = useCallback(() => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { values, setFieldValue } = useFormikContext<any>();
    return (
      <div style={{ alignSelf: "self-start" }}>
        <SyncButton
          type="button"
          disabled={resyncing}
          onClick={async () => {
            const formCnpj = HelperMask.removeCnpjMask(values?.cnpj || "");
            const currentCnpj = HelperMask.removeCnpjMask(companyCnpjRef.current || "");

            if (!HelperCnpjInformaton.isValidCnpj(formCnpj)) {
              toastUtils.showMessage({
                type: "warning",
                message: "CNPJ inválido ou não encontrado.",
              });
              return;
            }

            if (formCnpj !== currentCnpj && currentCnpj !== "") {
              setPendingCnpj(formCnpj);
              setFieldValueRef.current = setFieldValue;
              setShowConfirmModal(true);
            } else {
              await executeSync(formCnpj, setFieldValue);
            }
          }}
          $resyncing={resyncing}
        >
          <FiRefreshCw />
          {resyncing ? "Buscando..." : "Sincronizar CNPJ"}
        </SyncButton>
      </div>
    );
  }, [resyncing, executeSync]);

  const NfseNacionalComponent = useCallback(
    () => <NfseNacionalCard mode="array" initialChecked />,
    [],
  );

  const dataForm = useMemo(() => [
    {
      items: [
        {
          name: "title",
          component: "Title",
          title: "Dados pessoais: ",
          number: 1,
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "2fr 1fr" },
          inputs: [
            {
              name: "name",
              component: "Input",
              value: "",
              title: "Nome",
              label: "Nome",
              required: true,
              validations: "inputRequired",
            },
            {
              component: "InputMask",
              mask: "CPF",
              name: "cpf",
              label: "CPF",
              title: "CPF",
              value: "",
              required: true,
              validations: "cpfValidation",
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: isSmallScreen ? "1fr 1fr" : "1fr 1fr 1fr",
          },
          inputs: [
            {
              name: "email",
              component: "Input",
              value: "",
              title: "E-mail",
              label: "E-mail",
              required: true,
              validations: "emailRequired",
            },
            {
              component: "InputMask",
              mask: "PHONE_NUMBER",
              name: "phone",
              label: "Telefone",
              title: "Telefone",
              value: "",
              required: true,
              validations: "inputRequired",
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: isSmallScreen ? "1fr 1fr" : "2fr 1fr",
          },
          hasDivider: true,
          inputs: [
            {
              name: "stacks",
              title: "Stacks",
              component: "Multiselect",
              styleContainer: { margin: "0px" },
              initialSelectedOptions: [],
              options: stacks
                .filter((stack) => stack.description !== "Não desejo informar")
                .map((stack) => ({
                  label: stack.description,
                  value: stack.id,
                })),
            },
            {
              component: "InputGroup",
              style: { display: "flex", flexDirection: "column" },
              inputs: [
                {
                  name: "otherStack",
                  component: "MultipleTextInput",
                  styleContainer: { margin: "0px" },
                  value: "",
                  title: "Outra stack",
                  label: "Outra stack",
                },
                {
                  name: "noStack",
                  component: "InputCheck",
                  title: "",
                  label: "",
                  onChange: (value: any, el: any) =>
                    handleNoStackChange(value, el, stacks),
                  options: [
                    {
                      value: "true",
                      label: "Não desejo informar",
                      name: "Não desejo informar",
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Endereço Residencial: ",
          number: 2,
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
          },
          inputs: [
            {
              name: "developerCep",
              component: "InputMask",
              mask: "CEP",
              label: "CEP",
              title: "CEP",
              value: "",
              required: true,
              validations: "inputRequired",
              onChange: (data: any, el: any) =>
                handleCepChange(data, el, "developer"),
            },
            {
              component: "Input",
              name: "developerStreet",
              label: "Rua",
              title: "Rua",
              value: "",
              required: true,
              validations: "inputRequired",
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr" },
          inputs: [
            {
              name: "developerNumber",
              component: "Input",
              label: "Número",
              title: "Número",
              value: "",
              required: true,
              validations: "inputRequired",
            },
            {
              name: "developerNeighborhood",
              component: "Input",
              label: "Bairro",
              title: "Bairro",
              value: "",
              required: true,
              validations: "inputRequired",
            },
            {
              name: "developerComplement",
              component: "Input",
              label: "Complemento",
              title: "Complemento",
              value: "",
              required: false,
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr" },
          hasDivider: true,
          inputs: [
            {
              name: "developerCity",
              component: "Input",
              label: "Cidade",
              title: "Cidade",
              value: "",
              required: true,
              validations: "inputRequired",
            },
            {
              name: "developerState",
              component: "Input",
              label: "Estado",
              title: "Estado",
              value: "",
              required: true,
              validations: "inputRequired",
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Dados CNPJ: ",
          number: 3,
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: "10px",
            alignItems: "end",
          },
          inputs: [
            {
              name: "socialReason",
              component: "Input",
              label: "Razão Social",
              title: "Razão Social",
              value: "",
              required: true,
              validations: "inputRequired",
            },
            {
              component: "InputMask",
              mask: "CNPJ",
              name: "cnpj",
              label: "CNPJ",
              title: "CNPJ",
              value: "",
              onChange: (data: any, el: any) => handleCnpjChange(data, el),
              required: true,
              validations: "cpnjValidation",
            },
            {
              component: SyncButtonComponent,
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            alignItems: "center",
          },
          inputs: [
            {
              name: "stateRegistration",
              component: "Input",
              label: "Inscrição Estadual",
              title: "Inscrição Estadual",
              value: "",
            },
            {
              name: "municipalRegistration",
              component: "Input",
              label: "Inscrição Municipal",
              title: "Inscrição Municipal",
              value: "",
            },
            {
              name: "isMei",
              component: "InputCheck",
              title: "",
              label: "",
              options: [
                {
                  value: "true",
                  label: "O CNPJ é MEI?",
                  name: "O CNPJ é MEI?",
                },
              ],
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: "flex",
            width: "100%",
          },
          inputs: [
            {
              name: "companyActivities",
              component: "ActivitiesList",
              label: "Atividades Econômicas (CNAE)",
              title: "Atividades Econômicas (CNAE)",
              value: [],
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "2fr 1fr" },
          inputs: [
            {
              name: "fantasyName",
              component: "Input",
              label: "Nome Fantasia",
              title: "Nome Fantasia",
              value: "",
              required: true,
              validations: "inputRequired",
            },
            {
              component: "Input",
              name: "companyEmail",
              label: "E-mail Cadastrado",
              title: "E-mail Cadastrado",
              value: "",
              required: true,
              validations: "emailRequired",
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr" },
          inputs: [
            {
              name: "companyPhone",
              component: "InputMask",
              mask: "PHONE_NUMBER",
              label: "Telefone",
              title: "Telefone",
              placeholder: "(00) 0000-0000",
              value: "",
              required: true,
              validations: "phoneNumberRequired",
            },
            {
              name: "companyMobilePhone",
              component: "InputMask",
              mask: "MOBILE_NUMBER",
              label: "Celular",
              title: "Celular",
              placeholder: "(00) 00000-0000",
              value: "",
            },
            {
              name: "taxType",
              component: "Select",
              label: "Regime Tributário",
              title: "Regime Tributário",
              value: "",
              required: true,
              validations: "inputRequired",
              onChange: (value: string, setFieldValue: any) => {
                if (setFieldValue && (value === "SIMPLES_NACIONAL" || value === "SIMPLES_NACIONAL_EXCESSO")) {
                  setFieldValue("simplesNacional", ["true"]);
                }
              },
              options: taxTypeList.map((tax) => ({
                value: tax.ENUM,
                label: tax.label,
              })),
            },
            {
              name: "especialTaxType",
              component: "Select",
              label: "Regime Tributário Especial",
              title: "Regime Tributário Especial",
              value: "",
              required: true,
              validations: "inputRequired",
              options: especialTaxTypeList.map((tax) => ({
                value: tax.ENUM,
                label: tax.label,
              })),
            },
          ],
        },
        {
          component: "InputGroup",
          hasDivider: true,
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr" },
          inputs: [
            {
              name: "simplesNacional",
              component: "InputCheck",
              title: "",
              label: "",
              options: [
                {
                  value: "true",
                  label: "Optante pelo Simples Nacional",
                  name: "Optante pelo Simples Nacional",
                },
              ],
            },
            {
              name: "culturalIncentive",
              component: "InputCheck",
              title: "",
              label: "",
              options: [
                {
                  value: "true",
                  label: "Incentivo a Cultura",
                  name: "Incentivo a Cultura",
                },
              ],
            },
            {
              name: "taxIncentive",
              component: "InputCheck",
              title: "",
              label: "",
              options: [
                {
                  value: "true",
                  label: "Taxa de incentivo",
                  name: "Taxa de incentivo",
                },
              ],
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Nota Fiscal Nacional: ",
          number: 4,
        },
        {
          component: "InputGroup",
          hasDivider: true,
          style: { display: "grid", gridTemplateColumns: "1fr" },
          inputs: [
            {
              name: "nfseNacional",
              component: NfseNacionalComponent,
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Prefeitura: ",
          number: 5,
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
          },
          hasDivider: true,
          inputs: [
            {
              name: "cityHallLogin",
              component: "Input",
              label: "Login da Prefeitura",
              title: "Login da Prefeitura",
              value: "",
            },
            {
              name: "cityHallPassword",
              component: "InputPassword",
              type: "password",
              label: "Senha da Prefeitura",
              title: "Senha da Prefeitura",
              value: "",
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Certificado Digital: ",
          number: 6,
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
          },
          hasDivider: true,
          inputs: [
            {
              name: "certificateFile",
              component: "InputUpload",
              label: "Certificado Digital",
              title: "Certificado Digital",
              value: "",
              accept: ".pfx",
            },
            {
              name: "certificatePassword",
              component: "InputPassword",
              type: "password",
              label: "Senha do Certificado",
              title: "Senha do Certificado",
              value: "",
            },
            {
              name: "certificateEmail",
              component: "Input",
              label: "E-mail Cadastrado",
              title: "E-mail Cadastrado",
              value: "",
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Endereço do CNPJ: ",
          number: 7,
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
          },
          inputs: [
            {
              name: "companyCep",
              component: "InputMask",
              mask: "CEP",
              label: "CEP",
              title: "CEP",
              value: "",
              required: true,
              validations: "inputRequired",
              onChange: (data: any, el: any) =>
                handleCepChange(data, el, "company"),
            },
            {
              name: "tipoLogradouro",
              component: "Select",
              label: "Tipo de Logradouro",
              title: "Tipo de Logradouro",
              value: "",
              required: true,
              validations: "inputRequired",
              options: addressTypeList.map((address) => ({
                value: address.ENUM,
                label: address.label,
              })),
            },
            {
              component: "Input",
              name: "companyStreet",
              label: "Rua",
              title: "Rua",
              value: "",
              required: true,
              validations: "inputRequired",
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr" },
          inputs: [
            {
              name: "companyNumber",
              component: "Input",
              label: "Número",
              title: "Número",
              value: "",
              required: true,
              validations: "inputRequired",
            },
            {
              name: "companyNeighborhood",
              component: "Input",
              label: "Bairro",
              title: "Bairro",
              value: "",
              required: true,
              validations: "inputRequired",
            },
            {
              name: "companyComplement",
              component: "Input",
              label: "Complemento",
              title: "Complemento",
              value: "",
              required: false,
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr" },
          hasDivider: true,
          inputs: [
            {
              name: "companyCity",
              component: "Input",
              label: "Cidade",
              title: "Cidade",
              value: "",
              required: true,
              validations: "inputRequired",
            },
            {
              name: "companyState",
              component: "Input",
              label: "Estado",
              title: "Estado",
              value: "",
              required: true,
              validations: "inputRequired",
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Plano: ",
          number: 8,
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: isSmallScreen ? "1fr 1fr" : "1fr 1fr 1fr",
          },
          hasDivider: true,
          inputs: [
            {
              name: "plan",
              component: "Select",
              required: true,
              validations: "inputRequired",
              label: "Plano",
              title: "Plano",
              value: "",
              options: packages.map((pack) => ({
                value: pack.id,
                label: pack.packageEnum,
              })),
            },
            {
              name: "planStatus",
              component: "Select",
              label: "Status",
              title: "Status",
              required: true,
              validations: "inputRequired",
              value: "",
              options: planStatusList,
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "flex", gap: "16px", width: "100%", marginTop: 16 },
          inputs: [
            {
              name: "backButton",
              component: "Button",
              type: "button",
              title: "Voltar",
              onClick: goBack,
              color: false,
            },
            {
              name: "button",
              component: "Button",
              title: "Enviar",
              submittingText: "Carregando...",
              color: true,
            },
          ],
        },
      ],
    },
  ], [isSmallScreen, stacks, packages, SyncButtonComponent, NfseNacionalComponent, goBack]);

  const props = useMemo(() => ({
    dataForm,
    onSubmit: handleSubmit,
    errorMessage,
  }), [dataForm, handleSubmit, errorMessage]);

  return (
    <>
      <SolicitationNew {...props} />
      <ConfirmDialogStyled
        visible={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        message="O CNPJ digitado é diferente do CNPJ sincronizado anteriormente. Deseja realizar uma nova sincronização? Isso substituirá os dados preenchidos."
        header="Confirmar sincronização de CNPJ"
        icon={() => <FiAlertCircle size={24} color={theme.colors.red} />}
        acceptLabel="Sim, sincronizar"
        rejectLabel="Não, cancelar"
        accept={() => {
          if (setFieldValueRef.current) {
            executeSync(pendingCnpj, setFieldValueRef.current);
          }
          setShowConfirmModal(false);
        }}
        reject={() => setShowConfirmModal(false)}
      />
    </>
  );
};

export default MakeSolicitationNew;
